/**
 * 所属区所マスタ (`depot-master.json`) のフェッチ (SBI-6.5)。
 *
 * - WebApp 内に同梱した depot-master.json を `/data/depot-master.json` 経由で読み込む。
 * - `routes-master.json` (routes.ts) と同じく CloudFront 配下の同居アセットとして配信する。
 * - `vehicle_id_to_depot` は「所属区所コード → 所属区所名 (depot_id)」のマッピング。
 *   key 側 (`"10"`/`"15"`/`"21"`) は VehicleTripMapping の PK 前半に埋め込まれる所属区所コード、
 *   value 側 (`"鎌倉車両"`/`"三鷹車両"`/`"さいたま車両"`) が depot_id (所属区所名) となる。
 *   通知先メール管理画面 (AlertRecipientPage) では、このマッピングの value から
 *   depot_id (所属区所名) のユニーク集合を抽出し、宛先登録時の選択肢として使う。
 */

export interface DepotMasterFile {
  /** 所属区所コード → 所属区所名 (depot_id) のマッピング (例: `{ "21": "さいたま車両" }`) */
  vehicle_id_to_depot: Record<string, string>;
}

/** depot-master.json を fetch して返す。 */
export async function fetchDepotMaster(): Promise<DepotMasterFile> {
  const base = import.meta.env.BASE_URL.endsWith('/')
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`;
  const res = await fetch(`${base}data/depot-master.json`);
  if (!res.ok) {
    throw new Error(`Failed to fetch depot-master.json: HTTP ${res.status}`);
  }
  return (await res.json()) as DepotMasterFile;
}

/**
 * depot マスタから depot_id (所属区所名) のユニーク集合を抽出して返す。
 *
 * - `vehicle_id_to_depot` の値 (所属区所名 = depot_id) を重複排除する。
 * - 空文字 / 非文字列は除外する。
 * - 表示順を安定させるため昇順ソートして返す。
 */
export function extractDepotIds(master: DepotMasterFile | undefined | null): string[] {
  const mapping = master?.vehicle_id_to_depot;
  if (!mapping) return [];
  const set = new Set<string>();
  for (const value of Object.values(mapping)) {
    if (typeof value === 'string' && value.trim()) {
      set.add(value.trim());
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}
